import { useEffect, useRef, type ReactElement, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { track } from '../../../../../shared-web/track';
import './Modal.css';

export interface ModalProps {
  onClose: () => void;
  /** id of the element that titles the dialog. */
  labelledBy: string;
  className?: string;
  /** Processing states may be modal without being safely dismissible. */
  dismissible?: boolean;
  /**
   * Put the opening focus on the dialog itself rather than on its first
   * control. For a dialog the person opened by clicking, focusing the action
   * is right. For one that ARRIVES on its own — a finalize outcome landing
   * while they sit and watch — it is not: the browser has no click to
   * attribute the focus to, so :focus-visible matches and the primary button
   * wears a keyboard ring nobody asked for. Focusing the dialog keeps screen
   * readers announcing it and Tab starting inside it, with no ring.
   */
  focusDialog?: boolean;
  /**
   * Dock the dialog to the bottom edge instead of centring it — the sheet
   * shape (see BottomSheet). Same dialog semantics either way: one primitive
   * owns focus, Escape and backdrop dismissal, and only the chrome changes.
   *
   * `'phone'` is the responsive form: a sheet where a thumb reaches the bottom
   * edge, and the ordinary centred card from 640 px up, where there is no
   * bottom edge to dock to.
   */
  sheet?: boolean | 'phone';
  /**
   * The chat's skin (chat-skin.css, 2026-10-20) for a dialog opened from a chat surface: it is portaled to the body,
   * outside the surface that wears the skin, so it carries the skin itself — at night a sheet over the Chats list is
   * night too. Paper (a letter, an Ember) never passes one.
   */
  skin?: 'sand' | 'night';
  children: ReactNode;
}

/**
 * Shared dialog primitive: role="dialog" + aria-modal, Escape and backdrop
 * click close, focus moves in on open and returns on close.
 */
export function Modal({
  onClose, labelledBy, className = '', dismissible = true, focusDialog = false,
  sheet = false, skin, children,
}: ModalProps): ReactElement {
  const bodyRef = useRef<HTMLDivElement>(null);
  // A backdrop tap is a press that began AND ended on the backdrop. The click
  // alone is not enough (2026-09-07): on a touch screen the browser synthesises
  // click ~30ms after the finger lifts, hit-testing the finger's point at that
  // moment — so a press that opened this dialog from underneath (pointerup on
  // the tree's letters) delivered its click onto the freshly mounted backdrop
  // and dismissed it in the same tap. The same rule keeps a drag that starts
  // in a textarea and ends outside from closing the dialog.
  const pressedBackdrop = useRef(false);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const target = focusDialog
      ? bodyRef.current
      : bodyRef.current?.querySelector<HTMLElement>(
        '[data-autofocus], textarea, input, button, a[href]',
      );
    target?.focus();
    const onKey = (e: KeyboardEvent): void => {
      // A dismissal is a door_close like the X (2026-09-23): before this the
      // backdrop and Escape closed a sheet without a row, so a person who opened
      // the registration sheet and tapped the dusk behind it read as "did nothing".
      if (dismissible && e.key === 'Escape') { track('ui.clicked', { name: 'door_close', discovery: 'escape' }); onClose(); }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      previous?.focus?.();
    };
    // onClose identity changes must not re-run focus handling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One modifier string for both halves of the dialog: the backdrop decides
  // where the card sits, the card carries its own chrome, and they must never
  // disagree about which shape this is.
  const shape = sheet ? ` is-sheet${sheet === 'phone' ? ' is-wide-centred' : ''}` : '';
  const node = (
    <div
      className={`ember-modal-backdrop${shape}`}
      onPointerDown={(e) => { pressedBackdrop.current = e.target === e.currentTarget; }}
      onClick={(e) => {
        const tap = pressedBackdrop.current && e.target === e.currentTarget;
        pressedBackdrop.current = false;
        if (dismissible && tap) { track('ui.clicked', { name: 'door_close', discovery: 'backdrop' }); onClose(); }
      }}
    >
      <div
        className={`ember-modal${shape}${className ? ` ${className}` : ''}`}
        data-chat-skin={skin}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        // Programmatic focus only — never a tab stop of its own.
        tabIndex={focusDialog ? -1 : undefined}
        ref={bodyRef}
      >
        {children}
      </div>
    </div>
  );
  return createPortal(node, document.body);
}
